import React, { useEffect, useState } from 'react';
import { Container, Typography, Card, CardContent, CircularProgress, Grid, Box, Paper, Divider, Button, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import Layout from './Layout';
import DeleteIcon from '@mui/icons-material/Delete';
import * as XLSX from 'xlsx';  // Import xlsx library
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { colors } from '../colors';
import { motion } from 'framer-motion';
import AdminNavigationPane from './AdminNavigationPane';

const SurveyDashboard = () => {
    const [currentComponent, setCurrentComponent] = useState('DashboardView');
    const { surveyId } = useParams();
    const navigate = useNavigate();
    const handleNavigate = (view) => {
        // Redirect to /admin/dashboard with the selected view
        navigate(`/admin/dashboard`, {state: {view}});
    };
    const [survey, setSurvey] = useState(null);
    const [surveyData, setSurveyData] = useState({
        totalResponses: 0,
        completedResponses: 0,
        totalSurvey: 0,
        responses: []
    });
    const [variables, setVariables] = useState([]);
    const [variableKey, setVariableKey] = useState('');
    const [variableValue, setVariableValue] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedResponses, setExpandedResponses] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [selectedVariableIndex, setSelectedVariableIndex] = useState(null);

    // State for Login Dialog
    const [openLoginDialog, setOpenLoginDialog] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const reportLink = `${window.location.origin}/survey/report/${surveyId}`;
    

    useEffect(() => {
        const fetchSurveyDetails = async () => {
            try {
                const response = await axiosInstance.get(`/api/surveys/${surveyId}`);
                setSurvey(response.data);

                if (response.data.variables) {
                    setVariables(response.data.variables);
                }

                const completedResponse = await axiosInstance.get(`/api/surveys/${surveyId}/completed`);
                setSurveyData(completedResponse.data);
            } catch (error) {
                setError('Error fetching survey details');
                console.error('Error:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSurveyDetails();
    }, [surveyId]);

    const handleCloneSurvey = async () => {
        try {
            const response = await axiosInstance.post(`/api/surveys/${surveyId}/clone`);
            if (response.status === 200) {
                navigate('/admin/dashboard');
                // Optionally navigate to the newly cloned survey or update the state
            }
        } catch (error) {
            console.error('Error cloning survey:', error);
            alert('Failed to clone the survey. Please try again.');
        }
    };

    const handleCopyReportLink = () => {
        navigator.clipboard.writeText(reportLink); // Copy the link to the clipboard
        setOpenSnackbar(true); // Show feedback message
    };

    const handleCloseSnackbar = () => setOpenSnackbar(false);


    const handleGenerateReport = async () => {
        try {
            const response = await axiosInstance.post('/api/reports/generate', {
                surveyId,
                username,
                password
            });

            if (response.status === 201) {
                alert('Access Granted!');
                setOpenLoginDialog(false);
            }
        } catch (error) {
            if (error.response && error.response.status === 409) {
                setLoginError('User ID already exists for this survey.');
            } else {
                setLoginError('Failed to grant access. Please try again.');
            }
        }
    };

    const handleOpenLoginDialog = () => {
        // Clear previous inputs when opening the dialog
        setUsername('');
        setPassword('');
        setLoginError('');
        setOpenLoginDialog(true);
    };
    const handleCloseLoginDialog = () => setOpenLoginDialog(false);

    // Calculate In Progress surveys
    const inProgressSurveys = surveyData.totalResponses - surveyData.completedResponses;

    // Toggle response expansion
    const toggleResponseExpansion = (responseId) => {
        setExpandedResponses((prev) => ({
            ...prev,
            [responseId]: !prev[responseId]
        }));
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress size={50} />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" variant="h6" align="center" mt={5}>
                {error}
            </Typography>
        );
    }

    // Add a variable (key-value pair) and update the backend immediately
    const addVariable = async () => {
        if (variableKey && variableValue) {
            const updatedVariables = [...variables, { key: variableKey, value: variableValue }];
            setVariables(updatedVariables);
            setVariableKey('');
            setVariableValue('');
            await handleSave(updatedVariables);
        }
    };

    // Handle opening the confirmation dialog
    const openDeleteDialog = (index) => {
        setSelectedVariableIndex(index);
        setOpenConfirmDialog(true);
    };

    // Handle closing the dialog
    const handleCloseDialog = () => {
        setOpenConfirmDialog(false);
        setSelectedVariableIndex(null);
    };

    // Handle delete variable after confirmation
    const handleDeleteVariable = async () => {
        const updatedVariables = variables.filter((_, i) => i !== selectedVariableIndex);
        setVariables(updatedVariables);
        await handleSave(updatedVariables);
        handleCloseDialog();
    };

    // Handle Save
    const handleSave = async (updatedVariables) => {
        try {
            setIsSaving(true);
            const updatedSurvey = {
                ...survey,
                variables: updatedVariables,
            };

            await axiosInstance.put(`/api/surveys/${surveyId}`, updatedSurvey);
            alert('Survey updated successfully!');
        } catch (error) {
            console.error('Error saving survey:', error);
            alert('Failed to save survey.');
        } finally {
            setIsSaving(false);
        }
    };

    const downloadSurveyResponses = async (surveyId) => {
        try {
            const response = await axiosInstance.get(`/api/export/${surveyId}/responses`, {
                responseType: 'blob', // Important for downloading files
            });

            // Create a link element to trigger the download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `survey_responses_${surveyId}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading responses:', error);
            alert('Failed to download responses. Please try again.');
        }
    };


    return (
        <Layout>
            <Box
                sx={{
                    backgroundColor: colors.primaryBannerBlue,
                    color: 'white',
                    padding: { xs: '5px 0', sm: '10px 0' }, // Responsive padding
                    textAlign: 'center',
                    fontSize: { xs: '1rem', sm: '1.2rem' },
                    fontWeight: '500',
                }}
            >
                Survey Data
            </Box>
            <motion.div initial="initial" animate="animate" transition={{ duration: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' }, // Stack vertically on small screens, side-by-side on large screens
                        width: '100%',
                        minHeight: '100vh',
                        backgroundColor: '#eaecee',
                        padding: { xs: '10px', sm: '20px', md: '20px' },
                        boxSizing: 'border-box', // Ensures padding is included in total width/height
                        gap: { xs: 2, sm: 3 }, // Consistent gap between sections
                    }}
                >
                    <AdminNavigationPane
                        currentPath="/admin/survey-dashboard"
                        onNavigate={handleNavigate} // Use React Router navigation
                    />
                    <Box
                        sx={{
                            flexGrow: 1, // Take up remaining space
                            padding: '20px',
                            backgroundColor: 'white',
                            borderRadius: '20px',
                            boxShadow: '0 6px 14px rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        {/* Render Components Dynamically */}
                        <Box
                            sx={{
                                flexGrow: 1, // Take up remaining space
                                padding: '20px',
                                backgroundColor: 'white',
                                borderRadius: '20px',
                                boxShadow: '0 6px 14px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#333', mb: 2, color: colors.racineRedDark }}>
                                    Survey Dashboard
                                </Typography>
                                <Box>
                                    <Typography variant="body2" sx={{ color: '#888', fontWeight: 'bold' }}>
                                        Valid Till: {new Date(survey.validTill).toLocaleDateString()}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: survey.active ? '#2e7d32' : '#d32f2f', fontWeight: 'bold' }}>
                                        Status: {survey.active ? 'Active' : 'Inactive'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic', mt: 0 }}>
                                        Survey ID: {surveyId}
                                    </Typography>
                                    <Box mt={0} display="flex" flexDirection="column" alignItems="flex-start">
                                        <Button
                                            onClick={handleCloneSurvey}
                                            variant="outlined"
                                            sx={{ color: '#1976d2', marginTop: 1 }}
                                        >
                                            Clone Survey
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                            <Divider />

                            {/* Login Dialog */}
                            <Dialog open={openLoginDialog} onClose={handleCloseLoginDialog}>
                                <DialogTitle>Manage Report Access</DialogTitle>
                                <DialogContent>
                                    <TextField
                                        label="Username"
                                        fullWidth
                                        margin="normal"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                    />
                                    <TextField
                                        label="Password"
                                        type="password"
                                        fullWidth
                                        margin="normal"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    {loginError && (
                                        <Typography color="error" variant="body2">
                                            {loginError}
                                        </Typography>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleGenerateReport} variant="contained" color="primary">
                                        Generate
                                    </Button>
                                    <Button onClick={handleCloseLoginDialog} variant="outlined">
                                        Cancel
                                    </Button>
                                </DialogActions>
                            </Dialog>

                            {/* Snackbar for Copy Feedback */}
                            <Snackbar
                                open={openSnackbar}
                                autoHideDuration={3000}
                                onClose={handleCloseSnackbar}
                                message="Report link copied!"
                            />

                            <Box mt={3}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: colors.primaryApplicationText }}>
                                    Survey Name
                                </Typography>
                                <Typography variant="body1" gutterBottom sx={{ color: colors.primaryApplicationText }}>
                                    {survey.surveyName}
                                </Typography>
                            </Box>

                            <Box mt={3}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: colors.primaryApplicationText }}>
                                    Survey Description
                                </Typography>
                                <Typography variant="body1" gutterBottom sx={{ color: colors.primaryApplicationText }}>
                                    {survey.surveyDescription}
                                </Typography>
                            </Box>
                            <Box mt={3} display="flex" flexDirection="column" alignItems="flex-start">
                                <Button
                                    onClick={handleCopyReportLink}
                                    variant="text"
                                    startIcon={<ContentCopyIcon />}
                                    sx={{ paddingX: 2, paddingY: 1, borderRadius: 2, textTransform: 'none', color: colors.racineRedDark }}
                                >
                                    {reportLink}
                                </Button>

                                <Button
                                    onClick={handleOpenLoginDialog}
                                    variant="outlined"
                                    sx={{ color: colors.racineRedDark, borderColor: '#bc2534' }}
                                >
                                    Manage Access
                                </Button>
                            </Box>

                            <Grid container spacing={3} sx={{ mt: 4 }}>
                                <Grid item xs={12} sm={3}>
                                    <Card sx={{ backgroundColor: '#e8f5e9', minHeight: '140px' }}>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ color: '#388e3c', fontWeight: 'bold' }}>
                                                Total Surveys
                                            </Typography>
                                            <Typography variant="h4" sx={{ color: '#2e7d32' }}>
                                                {surveyData.totalSurvey}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <Card sx={{ backgroundColor: '#ffebee', minHeight: '140px' }}>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
                                                Total Responses
                                            </Typography>
                                            <Typography variant="h4" sx={{ color: '#c62828' }}>
                                                {surveyData.totalResponses}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <Card sx={{ backgroundColor: '#e3f2fd', minHeight: '140px' }}>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                                Completed
                                            </Typography>
                                            <Typography variant="h4" sx={{ color: '#1565c0' }}>
                                                {surveyData.completedResponses}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <Card sx={{ backgroundColor: '#fff9c4', minHeight: '140px' }}>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ color: '#f9a825', fontWeight: 'bold' }}>
                                                In Progress
                                            </Typography>
                                            <Typography variant="h4" sx={{ color: '#f57f17' }}>
                                                {inProgressSurveys}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box mt={5}>
                            <Typography variant="h5" sx={{ color: colors.racineRedDark, mb: 3 }}>
                                Survey Variables
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={5}>
                                    <TextField
                                        fullWidth
                                        label="Variable Name"
                                        value={variableKey}
                                        onChange={(e) => setVariableKey(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <TextField
                                        fullWidth
                                        label="Variable Value"
                                        value={variableValue}
                                        onChange={(e) => setVariableValue(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={addVariable}
                                        sx={{
                                            backgroundColor: colors.racineRedDark,
                                            '&:hover': {
                                                backgroundColor: colors.racineRedLight, // Optional hover effect
                                            },
                                        }}
                                    >
                                        Add Variable
                                    </Button>

                                </Grid>
                            </Grid>

                            <Box mt={3}>
                                {variables.length > 0 ? (
                                    <ul>
                                        {variables.map((variable, index) => (
                                            <li key={index}>
                                                <strong>{variable.key}</strong>: {variable.value}
                                                <IconButton onClick={() => openDeleteDialog(index)} color="secondary">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <Typography variant="body2" color="textSecondary">
                                        No variables defined.
                                    </Typography>
                                )}
                            </Box>

                            {isSaving && (
                                <Box mt={3}>
                                    <Typography variant="body2" color="textSecondary">
                                        Saving...
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Dialog open={openConfirmDialog} onClose={handleCloseDialog}>
                            <DialogTitle>Delete Variable?</DialogTitle>
                            <DialogContent>
                                <Typography>This action cannot be undone. Are you sure you want to delete this variable?</Typography>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseDialog} variant="outlined">
                                    Cancel
                                </Button>
                                <Button onClick={handleDeleteVariable} variant="contained" color="error">
                                    Delete
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Box mt={5}>
                            <Typography variant="h5" sx={{ color: colors.racineRedDark, mb: 3 }}>
                                Survey Responses
                            </Typography>
                            {surveyData.responses.length > 0 ? (
                                <Grid container spacing={3}>
                                    {surveyData.responses.map((response, index) => (
                                        <Grid item xs={12} key={response.id}>
                                            <Card sx={{ backgroundColor: '#fafafa', p: 2, borderRadius: '10px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)' }}>
                                                <Button onClick={() => toggleResponseExpansion(response.id)} variant="text" sx={{ color: colors.primaryApplicationText }}>
                                                    {index + 1}. {response.participantId} ({response.completed ? 'Completed' : 'In Progress'}) {expandedResponses[response.id] ? "▲" : "▼"}
                                                </Button>
                                                {expandedResponses[response.id] && (
                                                    <>
                                                        <Typography variant="body2" sx={{ color: '#888' }}>
                                                            Last Completed Section: {response.lastCompletedSection}
                                                        </Typography>
                                                        <Box mt={2}>
                                                            <Typography variant="body2" fontWeight="bold" sx={{ color: '#444' }}>
                                                                Section Responses:
                                                            </Typography>
                                                            <Box
                                                                component="pre"
                                                                sx={{
                                                                    backgroundColor: '#f4f4f4',
                                                                    padding: '10px',
                                                                    borderRadius: '4px',
                                                                    fontFamily: 'monospace',
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordWrap: 'break-word',
                                                                }}
                                                            >
                                                                {JSON.stringify(response.sectionResponses, null, 2)}
                                                            </Box>
                                                        </Box>
                                                        <Typography variant="body2" sx={{ color: '#888' }}>
                                                            Comment: {response.comment}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    No responses available.
                                </Typography>
                            )}

                            {/* Button to download responses */}
                            <Box mt={3}>
                                <Button
                                    variant="contained"
                                    onClick={() => downloadSurveyResponses(surveyId)}
                                    sx={{
                                        backgroundColor: colors.racineRedDark, // Custom color
                                        color: '#fff', // Text color
                                        '&:hover': {
                                            backgroundColor: colors.racineRedLight, // Hover effect
                                        },
                                    }}
                                >
                                    Download Responses
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </motion.div>
        </Layout>
    );
};

export default SurveyDashboard;
